import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  List,
  Box,
  Switch,
  Typography,
  useTheme,
  Stack,
  Checkbox,
} from "@mui/material";
import * as React from "react";
import { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyLoading } from "../../../shared-components/Loading";
import { alpha } from "@mui/material/styles";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { FileCard } from "../../../shared-components/FileCard";
import { SubjectYearGroupPicker } from "./SubjectYearGroupPicker";
import { MESSAGE_CATEGORIES } from "../../../shared-components/LoadingMessages";
import { useAutoGenerateQuiz } from "../../quiz/hooks/useAutoGenerateQuiz";
import { useAutoGenerateAssignment } from "../../assignment/hooks/useAutoGenerateAssignment";

export const QuestionExtraction = ({
  open,
  setOpen,
  onUpdate,
  subject,
  yearGroup,
  setExtracting,
  details,
  onRefresh,
  singleQuestion = false,
  assignmentOrQuizId = "",
  quizCreate = false,
  assignmentCreate = false,
}) => {
  if (assignmentCreate && quizCreate) {
    throw Error("Only one of assignmentCreate or quizCreate can be provided");
  }
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [jobId, setJobId] = useState(null);
  const theme = useTheme();

  const [includesMarkScheme, setIncludesMarkScheme] = useState(true);
  const ApiClient = useApiClient();

  const [subjects, setSubjects] = useState([]);
  const [singleQuestionOverride, setSingleQuestionOverride] = useState(false);
  const [educationLevels, setEducationLevels] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedEducationLevel, setSelectedEducationLevel] = useState(null);
  const [selectedYearGroup, setSelectedYearGroup] = useState(null);

  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const handleClose = () => {
    setOpen(false);
    setFiles([]);
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = selectedFiles.filter((file) =>
      allowedTypes.includes(file.type),
    );

    setFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    handleFileUpload({ target: { files: event.dataTransfer.files } });
  };

  const handleDeleteFile = (indexToDelete) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToDelete));
  };

  useEffect(() => {
    if (jobId) {
      setExtracting(true);
    } else {
      setExtracting(false);
    }
  }, [jobId]);

  // Fetch subjects and levels only if subject/yearGroup are not provided
  useEffect(() => {
    const needsContext = !subject || !yearGroup;
    if (!needsContext) return;
    const fetch = async () => {
      try {
        const response = await ApiClient.core.getSubjectsAndLevels();
        if (response) {
          setSubjects(response.subjects || []);
          setEducationLevels(response.educationLevels || []);
        }
      } catch (e) {
        console.error("Failed to fetch subjects/levels", e);
      }
    };
    fetch();
  }, [subject, yearGroup]);

  // Update year groups when a level is selected
  useEffect(() => {
    if (selectedEducationLevel) {
      setYearGroups(selectedEducationLevel.yearGroups || []);
    } else {
      setYearGroups([]);
      setSelectedYearGroup(null);
    }
  }, [selectedEducationLevel]);

  useEffect(() => {
    // TODO: remove this to use the hook in addQuestionStep
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const response = await ApiClient.core.getJobStatus({ jobId });
        onUpdate(response.results, {
          progress: response.progress,
          message: response.message,
        });
        if (
          response.status.toLowerCase() === "completed" ||
          response.status.toLowerCase() === "failed"
        ) {
          clearInterval(interval);
          setJobId(null);
          // optionally fetch new questions or notify user
        }
      } catch (err) {
        console.error("Error polling job:", err);
        clearInterval(interval);
        setJobId(null);
      }
    }, 2000); // poll every 2s

    return () => clearInterval(interval);
  }, [jobId]);

  const { autoGenerateQuiz } = useAutoGenerateQuiz();
  const { autoGenerateAssignment } = useAutoGenerateAssignment();

  const handleSubmit = async () => {
    if (quizCreate && !assignmentOrQuizId) {
      await autoGenerateQuiz({
        quizDetails: details,
        context: "",
        payload: "",
        files: files,
        onSameQuiz: onRefresh,
      });
      setOpen(false);
      return;
    }

    if (assignmentCreate && !assignmentOrQuizId) {
      await autoGenerateAssignment({
        details,
        files: files,
        onSameAssignment: onRefresh,
        includesMarkScheme: includesMarkScheme,
        singleQuestion: singleQuestion || singleQuestionOverride,
      });
      setOpen(false);
      return;
    }

    setSubmitting(true);
    try {
      const finalSubject = subject || selectedSubject;
      const finalYearGroup = yearGroup || selectedYearGroup;
      const response = await ApiClient.question.extractQuestionsAndMarkSchemes({
        files: files,
        subject: finalSubject.id,
        yearGroup: finalYearGroup.id,
        includesMarkScheme: includesMarkScheme,
        singleQuestion: singleQuestion || singleQuestionOverride,
        quizGroup: quizCreate ? assignmentOrQuizId : null,
        assignment: assignmentCreate ? assignmentOrQuizId : null,
      });
      if (response.success) {
        enqueueSnackbar("Successfully submitted, submit another?", {
          variant: "success",
          autoHideDuration: 3000,
        });
        onRefresh?.();
        setJobId(response.job.id);
        setFiles([]);
        setOpen(false);
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar("Error submitting, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableScrollLock
    >
      <DialogTitle>Upload and let AI help</DialogTitle>
      <DialogContent>
        {submitting ? (
          <FruitifyLoading category={MESSAGE_CATEGORIES.aiMagic} />
        ) : (
          <React.Fragment>
            {(!subject || !yearGroup) && (
              <SubjectYearGroupPicker
                subjects={subjects}
                educationLevels={educationLevels}
                yearGroups={yearGroups}
                selectedSubject={selectedSubject}
                setSelectedSubject={setSelectedSubject}
                selectedEducationLevel={selectedEducationLevel}
                setSelectedEducationLevel={setSelectedEducationLevel}
                selectedYearGroup={selectedYearGroup}
                setSelectedYearGroup={setSelectedYearGroup}
              />
            )}
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{ mt: 2 }}
            >
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includesMarkScheme}
                      onChange={(e) => setIncludesMarkScheme(e.target.checked)}
                    />
                  }
                  label="Includes Mark Scheme(s)"
                />
              </FormGroup>
              {!singleQuestion && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={singleQuestionOverride}
                      onChange={(e) =>
                        setSingleQuestionOverride(e.target.checked)
                      }
                    />
                  }
                  label="Single Question"
                />
              )}
            </Stack>
            <Box
              variant="outlined"
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleDrop(e);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              sx={{
                padding: 2,
                textAlign: "center",
                border: 1,
                borderStyle: "dashed",
                borderColor: dragging
                  ? theme.palette.primary.main
                  : alpha(theme.palette.text.secondary, 0.5),
                borderRadius: (theme.vars || theme).shape.borderRadius,
                backgroundColor: dragging
                  ? alpha(theme.palette.primary.main, 0.05)
                  : "transparent",
                transition: "border-color 150ms, background-color 150ms",
                cursor: "pointer",
                mb: 2,
                mt: 2,
                minHeight: "120px", // or use '30vh' for ~30% of viewport
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <FruitifyIcon name={"Assignment"} fontSize="large" />
              <Typography variant="subtitle1">
                Choose a file or drag and drop it here.
              </Typography>
              <Typography variant="body2">
                PDF or docx formats, up to 50MB{" "}
              </Typography>
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ display: "none" }}
                id="file-upload"
                accept=".pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              />
              <label htmlFor="file-upload">
                <Button variant="card-action" component="span" sx={{ mt: 2 }}>
                  Choose Files
                </Button>
              </label>
            </Box>
            {files.length > 0 && (
              <List>
                {files.map((file, index) => (
                  <FileCard
                    key={index}
                    file={file}
                    index={index}
                    handleDeleteFile={handleDeleteFile}
                  />
                ))}
              </List>
            )}
          </React.Fragment>
        )}
      </DialogContent>

      {!submitting && (
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              files.length === 0 ||
              // if parent didn't provide both, require local selections
              ((!subject || !yearGroup) &&
                (!selectedSubject || !selectedYearGroup))
            }
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
